import { useId } from "react";
import { Loader2, MapPin, Crosshair, Radio, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDistanceForDisplay } from "@/lib/geolocation";

const MAP_W = 320;
const MAP_H = 208;

type Layout = {
  pinX: number;
  pinY: number;
  userX: number | null;
  userY: number | null;
  accR: number;
};

function computeLayout(
  propertyLat: number,
  propertyLng: number,
  userLat: number | null,
  userLng: number | null,
  accuracyM: number | null,
  distanceM: number | null
): Layout {
  const latC =
    userLat != null && userLng != null ? (propertyLat + userLat) / 2 : propertyLat;
  const lngC =
    userLat != null && userLng != null ? (propertyLng + userLng) / 2 : propertyLng;
  const mPerLat = 111320;
  const mPerLng = 111320 * Math.cos((latC * Math.PI) / 180);

  const toXY = (lat: number, lng: number) => ({
    x: (lng - lngC) * mPerLng,
    y: -(lat - latC) * mPerLat,
  });

  const p = toXY(propertyLat, propertyLng);
  const u = userLat != null && userLng != null ? toXY(userLat, userLng) : null;

  const d = distanceM != null && Number.isFinite(distanceM) ? Math.max(0, distanceM) : 0;
  const acc = accuracyM != null && Number.isFinite(accuracyM) ? Math.max(8, accuracyM) : 55;

  let spanM = Math.max(d * 2.6, acc * 2.35, 95);
  if (u) {
    spanM = Math.max(spanM, Math.hypot(u.x - p.x, u.y - p.y) * 2.4, 95);
  }
  spanM = Math.min(spanM, 520);

  const scale = (Math.min(MAP_W, MAP_H) * 0.38) / spanM;

  const pinX = MAP_W / 2 + p.x * scale;
  const pinY = MAP_H / 2 + p.y * scale;
  const userX = u ? MAP_W / 2 + u.x * scale : null;
  const userY = u ? MAP_H / 2 + u.y * scale : null;
  const accR = Math.min(Math.max(acc * scale, 14), 88);

  return { pinX, pinY, userX, userY, accR };
}

export interface PreVisitLocationMapPanelProps {
  propertyLat: number;
  propertyLng: number;
  liveFix: { lat: number; lng: number; accuracyM: number } | null;
  isLocating: boolean;
  distanceM: number | null;
  goodSignal: boolean;
  canConfirmVerified: boolean;
  isFar: boolean;
  isSubmitting: boolean;
  /** True while a GPS refresh (Retry / re-center) is in flight */
  isRetrying?: boolean;
  onRetry: () => void;
  onRecenter: () => void;
  onConfirmVerified: () => void;
  onConfirmAnyway: () => void;
  onGetDirections: () => void;
  t: (key: string) => string;
}

/**
 * Pre-visit map: single non-interactive schematic (property pin, your position, accuracy).
 * No third-party map embeds — avoids promo/footer UI and markers sliding when users pan an iframe.
 */
export function PreVisitLocationMapPanel({
  propertyLat,
  propertyLng,
  liveFix,
  isLocating,
  distanceM,
  goodSignal,
  canConfirmVerified,
  isFar,
  isSubmitting,
  isRetrying = false,
  onRetry,
  onRecenter,
  onConfirmVerified,
  onConfirmAnyway,
  onGetDirections,
  t,
}: PreVisitLocationMapPanelProps) {
  const svgUid = useId().replace(/:/g, "");
  const gradId = `pvMapBg-${svgUid}`;
  const gridId = `pvMapGrid-${svgUid}`;

  const uLat = liveFix?.lat ?? null;
  const uLng = liveFix?.lng ?? null;
  const acc = liveFix?.accuracyM ?? null;
  const layout = computeLayout(propertyLat, propertyLng, uLat, uLng, acc, distanceM);

  const distLabel =
    distanceM != null && Number.isFinite(distanceM) ? formatDistanceForDisplay(distanceM) : "—";
  const accRounded = acc != null && Number.isFinite(acc) ? Math.round(acc) : null;

  const showLowAccuracyBanner =
    !!liveFix && !goodSignal && !isFar && distanceM != null && Number.isFinite(distanceM);

  return (
    <div className="space-y-3 text-left">
      <div
        className="relative overflow-hidden rounded-[var(--pv-r-lg)] border shadow-[var(--pv-shadow-card)]"
        style={{
          borderColor: "var(--pv-line)",
          background: "var(--pv-surface-2)",
          minHeight: MAP_H,
        }}
      >
        <svg
          width="100%"
          height={MAP_H}
          viewBox={`0 0 ${MAP_W} ${MAP_H}`}
          className="relative z-0 block"
          role="img"
          aria-label={t("preVisitMapAria")}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#e6f3f1" />
              <stop offset="55%" stopColor="#e4e9ea" />
              <stop offset="100%" stopColor="#dce3e2" />
            </linearGradient>
            <pattern id={gridId} width="18" height="18" patternUnits="userSpaceOnUse">
              <path
                d="M 18 0 L 0 0 0 18"
                fill="none"
                stroke="var(--pv-line)"
                strokeWidth="0.45"
                opacity={0.4}
              />
            </pattern>
          </defs>
          <rect width={MAP_W} height={MAP_H} fill={`url(#${gradId})`} />
          <rect width={MAP_W} height={MAP_H} fill={`url(#${gridId})`} opacity={0.5} />
          {/* Light “blocks” for depth (decorative, not geographic) */}
          <rect x="14" y="148" width="52" height="22" rx="3" fill="var(--pv-line)" opacity="0.12" />
          <rect x="248" y="36" width="58" height="34" rx="3" fill="var(--pv-line)" opacity="0.1" />
          <rect x="210" y="162" width="44" height="20" rx="3" fill="var(--pv-teal)" opacity="0.06" />

          {layout.userX != null && layout.userY != null && (
            <>
              <line
                x1={layout.pinX}
                y1={layout.pinY}
                x2={layout.userX}
                y2={layout.userY}
                stroke="var(--pv-teal)"
                strokeWidth="2"
                strokeDasharray="6 5"
                opacity={0.88}
              />
              <circle
                cx={layout.userX}
                cy={layout.userY}
                r={layout.accR}
                fill="var(--pv-warn-soft)"
                stroke="var(--pv-warn)"
                strokeWidth="1.5"
                opacity={goodSignal ? 0.24 : 0.4}
              />
              <circle cx={layout.userX} cy={layout.userY} r={7} fill="#2563eb" stroke="#fff" strokeWidth="2" />
            </>
          )}

          <g transform={`translate(${layout.pinX}, ${layout.pinY})`}>
            <path
              d="M0,-11 C-6,-11 -11,-5 -11,2 C-11,9 0,18 0,18 C0,18 11,9 11,2 C11,-5 6,-11 0,-11 Z"
              fill="var(--pv-teal)"
              stroke="var(--pv-surface)"
              strokeWidth="1.5"
            />
            <circle cy="-2" r="3" fill="var(--pv-ink-on-accent)" />
          </g>
        </svg>

        {isLocating && (
          <div
            className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 px-6 text-center"
            style={{ background: "rgba(245,246,247,0.94)" }}
          >
            <Loader2 className="h-8 w-8 pv-spin shrink-0" style={{ color: "var(--pv-teal)" }} />
            <p className="text-[15px] font-semibold" style={{ color: "var(--pv-ink)" }}>
              {t("preVisitLocatingTitle")}
            </p>
            <p className="text-xs leading-relaxed max-w-[240px]" style={{ color: "var(--pv-ink-2)" }}>
              {t("preVisitLocatingHint")}
            </p>
          </div>
        )}

        {isRetrying && !isLocating && (
          <div
            className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.5)" }}
          >
            <Loader2 className="h-9 w-9 pv-spin shrink-0" style={{ color: "var(--pv-teal)" }} />
          </div>
        )}

        <button
          type="button"
          disabled={isRetrying}
          className="pv-tappable absolute right-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border shadow-sm disabled:opacity-50"
          style={{
            borderColor: "var(--pv-line)",
            background: "var(--pv-surface)",
            color: "var(--pv-ink-2)",
          }}
          onClick={onRecenter}
          aria-label={t("preVisitRecenterAria")}
        >
          <Crosshair className="h-4 w-4" />
        </button>

        <p
          className="pointer-events-none absolute bottom-7 left-2 right-2 z-20 text-center text-[10px] font-normal normal-case leading-snug"
          style={{ color: "var(--pv-ink-3)" }}
        >
          {t("preVisitMapSchematicNote")}
        </p>
        <p
          className="pointer-events-none absolute bottom-1.5 right-2 z-20 max-w-[85%] text-right text-[10px] font-medium uppercase leading-tight tracking-wide"
          style={{ color: "var(--pv-ink-3)" }}
        >
          {t("preVisitMapLegend")}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div
          className="rounded-[var(--pv-r-md)] border px-3 py-3 shadow-[var(--pv-shadow-card)]"
          style={{ borderColor: "var(--pv-line)", background: "var(--pv-surface)" }}
        >
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--pv-ink-3)" }}>
            <MapPin className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--pv-teal)" }} aria-hidden />
            {t("preVisitDistanceLabel")}
          </div>
          <p className="text-lg font-bold leading-tight" style={{ color: "var(--pv-ink)" }}>
            {distLabel === "—" ? "—" : `${distLabel} ${t("preVisitAwaySuffix")}`}
          </p>
        </div>
        <div
          className="rounded-[var(--pv-r-md)] border px-3 py-3 shadow-[var(--pv-shadow-card)]"
          style={{ borderColor: "var(--pv-line)", background: "var(--pv-surface)" }}
        >
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide">
            <Radio
              className="h-3.5 w-3.5 shrink-0"
              style={{ color: goodSignal ? "var(--pv-teal)" : liveFix ? "var(--pv-warn)" : "var(--pv-ink-3)" }}
              aria-hidden
            />
            <span style={{ color: goodSignal ? "var(--pv-teal)" : liveFix ? "var(--pv-warn)" : "var(--pv-ink-3)" }}>
              {!liveFix ? t("preVisitSignalAwaiting") : goodSignal ? t("preVisitSignalGood") : t("preVisitSignalLow")}
            </span>
          </div>
          <p className="text-lg font-bold tabular-nums leading-tight" style={{ color: "var(--pv-ink-2)" }}>
            {accRounded != null ? `±${accRounded} m` : t("preVisitWaitingGpsShort")}
          </p>
        </div>
      </div>

      {liveFix && !isFar && goodSignal && (
        <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "var(--pv-teal)" }}>
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {t("preVisitWithinRange")}
        </div>
      )}

      {isFar && liveFix && (
        <div className="space-y-2">
          <div
            className="flex items-start gap-2 rounded-[var(--pv-r-md)] border px-3 py-2.5 text-sm"
            style={{ borderColor: "var(--pv-warn)", background: "var(--pv-warn-soft)", color: "var(--pv-warn)" }}
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t("preVisitFarFromProperty")}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-[var(--pv-r-md)]"
            style={{ borderColor: "var(--pv-line)" }}
            onClick={onGetDirections}
          >
            <MapPin className="mr-2 h-4 w-4" />
            {t("preVisitGetDirections")}
          </Button>
        </div>
      )}

      {showLowAccuracyBanner && (
        <div
          className="flex items-start gap-2 rounded-[var(--pv-r-md)] border px-3 py-2.5 text-sm"
          style={{ borderColor: "var(--pv-warn)", background: "var(--pv-warn-soft)", color: "var(--pv-warn)" }}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1 leading-snug">
            {t("preVisitLocationOffWarning").replace("{{acc}}", String(accRounded ?? ""))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isRetrying}
            className="shrink-0 gap-1 border-amber-700/30 bg-[var(--pv-surface)] text-amber-900 hover:bg-amber-50"
            onClick={onRetry}
          >
            {isRetrying ? (
              <Loader2 className="h-3.5 w-3.5 pv-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {isRetrying ? t("preVisitRetrying") : t("preVisitRetry")}
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-2 pt-1">
        <Button
          type="button"
          size="lg"
          disabled={
            isSubmitting ||
            !liveFix ||
            isLocating ||
            isFar ||
            !canConfirmVerified
          }
          className={cn(
            "flex h-14 w-full items-center justify-center gap-2 rounded-[var(--pv-r-xl)] text-base font-semibold pv-tappable border-0",
            canConfirmVerified && liveFix && !isFar
              ? "text-[var(--pv-ink-on-accent)] shadow-[var(--pv-shadow-elev)]"
              : ""
          )}
          style={{
            backgroundColor:
              canConfirmVerified && liveFix && !isFar ? "var(--pv-teal)" : "var(--pv-line)",
            color: canConfirmVerified && liveFix && !isFar ? "var(--pv-ink-on-accent)" : "var(--pv-ink-3)",
          }}
          onClick={onConfirmVerified}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 pv-spin shrink-0" />
              {t("preVisitConfirmingCta")}
            </>
          ) : !liveFix || isLocating ? (
            t("preVisitWaitingGpsCta")
          ) : (
            t("confirmLocation")
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          size="lg"
          disabled={
            isSubmitting ||
            !liveFix ||
            isLocating ||
            canConfirmVerified
          }
          className="h-12 w-full rounded-[var(--pv-r-lg)] text-sm font-semibold pv-tappable"
          style={{
            borderColor: "var(--pv-line)",
            background: "var(--pv-surface)",
            color: "var(--pv-ink-2)",
          }}
          onClick={onConfirmAnyway}
        >
          {t("preVisitConfirmAnyway")}
        </Button>
      </div>
    </div>
  );
}
